/**
 * Is this event target something the player is typing INTO?
 *
 * Every play screen listens on `window` for keystrokes, because the board is
 * the thing you type at and there is no input to focus. That listener has to
 * step aside for anything that is a real text field, or typing a message into
 * the chat also spells a word out on the board behind it — which is a bug this
 * app has actually shipped, and `CLAUDE.md` records the fix.
 *
 * **It lived in four places and one of them had drifted.** `LettersPlayScreen`,
 * `NumbersPlayScreen` and `useKeyboardMode` carried byte-identical copies;
 * `ConundrumPlayScreen`'s left `SELECT` out, so a select element was not
 * editable on exactly one screen. Nothing was visibly broken by that — the
 * conundrum screen may well have no select on it today — but a predicate that
 * answers differently in one copy is the shape of the original bug growing
 * back, and the next control added is what makes it real.
 *
 * `SELECT` is in the set deliberately. It takes keystrokes: typing jumps to a
 * matching option, and a screen that treated that as spelling would eat the
 * keypress and move the selection at the same time.
 */
export function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * Everything a keystroke can land on that will do something with it by itself.
 *
 * `isEditable` is the text half of the question and this is the whole of it. A
 * `<button>` activates on Enter and on Space, an `<a href>` on Enter, a
 * `<summary>` on both, and an element wearing `role="button"` is promising the
 * same contract to a screen reader whether or not it has wired one up. A window
 * listener that fires on one of those keys while one of them holds focus is
 * spending the press twice.
 */
const KEY_HANDLING_SELECTOR = [
  'button',
  'a[href]',
  'input',
  'textarea',
  'select',
  'summary',
  '[role="button"]',
  '[role="link"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="menuitem"]',
  '[role="tab"]',
].join(', ');

/**
 * Is this event target a control that will handle an activation key itself?
 *
 * The sibling of `isEditable`, for the screens whose window listener claims a
 * key a CONTROL wants rather than a key a text field wants. It is deliberately
 * wider than `isEditable`: it answers true for everything `isEditable` does, so
 * a caller that wants to stand aside for both asks once.
 *
 * It walks up with `closest` rather than testing the target's own tag, because
 * a button on this site is a box with a label inside it — a keydown's target is
 * the focused button, but a pointer-derived target is whatever child the finger
 * landed on, and the same predicate reads both. `NumbersPlayScreen` has had the
 * `closest('button, a')` form inline for its Enter for a while; this is that
 * check with the roles added and one place to fix it.
 *
 * WHETHER TO USE IT IS A JUDGEMENT ABOUT COST, NOT A HOUSE STYLE.
 * `LettersPlayScreen` deliberately does NOT consult it for Enter and takes that
 * key from the focused control every time; `ConundrumPlayScreen` deliberately
 * does for Space and stands aside. Both comments explain themselves at the call
 * site. Do not make them agree.
 */
export function isKeyHandlingControl(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (isEditable(target)) return true;
  return target.closest(KEY_HANDLING_SELECTOR) !== null;
}
